// Attachments for ⌘K: validate, keep screenshots legible but small enough to send, and make a thumbnail.
export const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
export const MAX_FILES = 3;
export const MAX_BYTES = 10 * 1024 * 1024;

export type Pending = { id: string; file: File; thumb: string; error?: string };

export async function prepare(file: File): Promise<Pending> {
  const id = Math.random().toString(36).slice(2);
  if (!ACCEPT.includes(file.type)) return { id, file, thumb: '', error: 'Only PNG, JPEG, WebP or PDF' };
  let out = file;
  // Very large screenshots are re-encoded (long edge ≤ 3000 px) so text stays readable and the upload stays small.
  if (file.type !== 'application/pdf' && file.size > 2.5 * 1024 * 1024) out = await shrink(file, 3000, 0.9).catch(() => file);
  if (out.size > MAX_BYTES) return { id, file: out, thumb: '', error: 'Over 10 MB' };
  // A failed preview never blocks the attachment itself.
  const thumb = file.type === 'application/pdf' ? '' : await thumbnail(out).catch(() => '');
  return { id, file: out, thumb };
}

async function load(file: File) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}

async function shrink(file: File, maxEdge: number, quality: number): Promise<File> {
  const img = await load(file);
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale);
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  const blob = await new Promise<Blob>((r) => c.toBlob((b) => r(b!), 'image/webp', quality));
  return new File([blob], file.name.replace(/\.\w+$/, '') + '.webp', { type: 'image/webp' });
}

async function thumbnail(file: File) {
  const img = await load(file);
  const scale = Math.min(1, 240 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * scale)); c.height = Math.max(1, Math.round(img.naturalHeight * scale));
  c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.72);
}

export function toForm(fields: Record<string, string>, items: Pending[]) {
  const f = new FormData();
  for (const [k, v] of Object.entries(fields)) f.append(k, v);
  items.forEach((p, i) => { f.append('files', p.file, p.file.name); if (p.thumb) f.append(`thumb${i}`, p.thumb); });
  return f;
}
